/**
 * ChatSessionContext — self-healing wrapper around bootstrapChatSession().
 *
 * Robustness goals:
 *   1. Strict-mode safe — no useRef "in-flight" guards that get stuck across
 *      a dev double-mount. Cleanup CANCELS the in-flight attempt cleanly.
 *   2. Retry-with-backoff on transient failure (network blip, cold-start
 *      latency, partial pod rollout). User never sees forever-spinner from
 *      a recoverable error.
 *   3. Re-bootstrap when userId changes (auth recovery → fresh guest).
 *   4. PATCH principal binding so downstream catalog enforcement /
 *      analytics see the right instance/partner identity.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../hooks/useAuth';
import { useInstance } from './InstanceContext';
import { bootstrapChatSession } from '../api/chatApi';
import { httpClient } from '../client';
import { emit as emitTelemetry } from '../utils/telemetry';
import logger from '../utils/logger';

export interface AgentIdentity {
  id?: string;
  displayName?: string;
  avatarUrl?: string;
}

export type ChatSessionStatus =
  | 'idle' // not yet authed; bootstrap hasn't been attempted
  | 'bootstrapping' // bootstrapChatSession in flight
  | 'ready' // chatSessionId + agent available
  | 'error'; // bootstrap exhausted retries; retry() to try again manually

interface ChatSessionContextValue {
  chatSessionId: string | null;
  agent: AgentIdentity | null;
  status: ChatSessionStatus;
  error: string | null;
  retry: () => void;
}

const ChatSessionContext = createContext<ChatSessionContextValue | undefined>(
  undefined,
);

// Retry policy: 5 attempts, exponential backoff with jitter, capped at 8s.
// Total worst-case wait ≈ 0.5 + 1 + 2 + 4 + 8 ≈ 15s before giving up. Most
// transient blips clear in <2s, so happy path takes one attempt.
const MAX_ATTEMPTS = 5;
const BASE_BACKOFF_MS = 500;
const MAX_BACKOFF_MS = 8000;

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));
const backoff = (attempt: number) =>
  Math.min(BASE_BACKOFF_MS * 2 ** attempt, MAX_BACKOFF_MS) *
  (0.5 + Math.random());

export const ChatSessionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { authState, sessionInfo } = useAuth();
  const { instanceId, parentOrigin, config } = useInstance();

  const [chatSessionId, setChatSessionId] = useState<string | null>(null);
  const [agent, setAgent] = useState<AgentIdentity | null>(null);
  const [status, setStatus] = useState<ChatSessionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState(0);

  // Track which userId we LAST successfully bootstrapped for. When userId
  // changes (post-deploy guest re-creation, sign-in/upgrade), we drop the
  // ref and let the bootstrap effect re-fire.
  const bootstrappedForUser = useRef<string | null>(null);

  const userId = sessionInfo?.userId ?? null;
  const isAuthed = authState === 'guest' || authState === 'authenticated';

  // Pull instance fields into refs so the bootstrap effect's deps stay tight
  // (only re-fire when isAuthed/userId/retryToken change). Without this the
  // PATCH-time read of `config.instance_id` could be stale, but config IS
  // settled by the time we get to bootstrap (auth comes later than resolve
  // in practice; even if not, the PATCH is best-effort).
  const instanceRefs = useRef({ instanceId, parentOrigin, config });
  instanceRefs.current = { instanceId, parentOrigin, config };

  useEffect(() => {
    if (!isAuthed) return;
    // INSTANCE DETERMINES THE AGENT — never the client (2026-07-09, product
    // owner): bootstrapping before /embedded/resolve lands left pinnedAgentId
    // null, and a signed-in visitor's most-recent session (their RN-app
    // agent, e.g. Sawyer) was reused instead of the instance agent. Wait for
    // the config; the effect re-fires when it arrives.
    if (!config) return;
    // Already succeeded for this userId? Don't re-bootstrap.
    if (bootstrappedForUser.current === userId) return;

    let cancelled = false;
    setStatus('bootstrapping');
    setError(null);

    (async () => {
      let lastError: unknown = null;
      for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
        if (cancelled) return;
        try {
          // Read the latest instance config — by the time we get here the
          // resolve call has typically landed (auth completes after resolve
          // in practice). If it hasn't yet, `pinnedAgentId` is null and we
          // fall back to the location-default agent; the user just sees
          // the generic name until next refresh.
          const cfg = instanceRefs.current.config;
          const persona = (cfg?.persona ?? {}) as {
            display_name?: unknown;
            avatar_url?: unknown;
          };
          const result = await bootstrapChatSession({
            pinnedAgentId: cfg?.agent_id ?? null,
            displayOverrides: {
              displayName:
                typeof persona.display_name === 'string'
                  ? persona.display_name
                  : null,
              avatarUrl:
                typeof persona.avatar_url === 'string'
                  ? persona.avatar_url
                  : null,
            },
          });
          if (cancelled) return;
          if (!result) {
            lastError = new Error('No chat session returned');
            // Treat null as a soft failure — retry.
          } else {
            // Success.
            bootstrappedForUser.current = userId;
            setChatSessionId(result.id);
            if (result.agent) {
              setAgent({
                id: result.agent.id,
                displayName: result.agent.displayName,
                avatarUrl: result.agent.avatarUrl,
              });
            }
            setStatus('ready');
            emitTelemetry('chat_session_started', { sessionId: result.id });

            // Best-effort principal binding. Read instance fields from ref
            // so we pick up resolveInstance results that landed during the
            // bootstrap roundtrip. Server set-once; safe to call after
            // re-bootstrap on auth recovery.
            const { instanceId, parentOrigin, config } = instanceRefs.current;
            if (instanceId && config?.instance_id) {
              try {
                await httpClient.patch(`/sessions/v1/${result.id}`, {
                  principal: {
                    instanceId: config.instance_id,
                    publicToken: instanceId,
                    appSurface: 'embed',
                    appVersion: __WG_EMBED_VERSION__,
                    sdkVersion: __WG_EMBED_VERSION__,
                    parentOrigin,
                  },
                });
              } catch (err) {
                logger.warn(
                  '[chat-session] principal binding PATCH failed',
                  err,
                );
              }
            }
            return;
          }
        } catch (err) {
          if (cancelled) return;
          lastError = err;
        }

        // Backoff before next attempt — but bail if cancelled mid-sleep.
        if (attempt < MAX_ATTEMPTS - 1) {
          logger.warn(
            `[chat-session] bootstrap attempt ${attempt + 1} failed, retrying`,
            lastError,
          );
          await sleep(backoff(attempt));
        }
      }

      // Exhausted retries.
      if (cancelled) return;
      logger.warn(
        '[chat-session] bootstrap exhausted retries — manual retry available',
        lastError,
      );
      setStatus('error');
      setError(
        (lastError as { message?: string })?.message ||
          'Failed to start chat session',
      );
    })();

    return () => {
      cancelled = true;
    };
    // Only the user-identity / explicit-retry changes should re-trigger this.
    // Instance fields are read via ref so they don't churn the effect.
  }, [isAuthed, userId, retryToken, config]);

  // User identity change → drop success cache + reset visible state. Triggers
  // the bootstrap effect (because bootstrappedForUser.current !== new userId).
  useEffect(() => {
    if (bootstrappedForUser.current && bootstrappedForUser.current !== userId) {
      setChatSessionId(null);
      setAgent(null);
      setStatus('idle');
      setError(null);
      bootstrappedForUser.current = null;
    }
  }, [userId]);

  // Late-arriving persona override. bootstrapChatSession() applies
  // displayOverrides synchronously off the instanceRefs at bootstrap time,
  // so when auth completes BEFORE /embedded/resolve does (common — auth
  // is a cheap guest-token issue, resolve does a DB roundtrip + listings
  // join), the override is empty and the agent's stock name (e.g.
  // "Leilani (Partner)") leaks into the header. When config lands later
  // with persona.display_name / persona.avatar_url, re-apply them onto
  // the cached agent so the header reflects the partner's chosen
  // identity without forcing a full re-bootstrap.
  const overrideDisplayName =
    typeof config?.persona?.display_name === 'string' &&
    config.persona.display_name.trim()
      ? config.persona.display_name.trim()
      : null;
  const overrideAvatarUrl =
    typeof config?.persona?.avatar_url === 'string' &&
    config.persona.avatar_url.trim()
      ? config.persona.avatar_url.trim()
      : null;
  useEffect(() => {
    if (!overrideDisplayName && !overrideAvatarUrl) return;
    setAgent(prev => {
      if (!prev) return prev;
      const wantsName = !!(
        overrideDisplayName && prev.displayName !== overrideDisplayName
      );
      const wantsAvatar = !!(
        overrideAvatarUrl && prev.avatarUrl !== overrideAvatarUrl
      );
      if (!wantsName && !wantsAvatar) return prev;
      return {
        ...prev,
        ...(wantsName ? { displayName: overrideDisplayName } : {}),
        ...(wantsAvatar ? { avatarUrl: overrideAvatarUrl } : {}),
      };
    });
    // `agent` in the deps closes the losing race: when resolve lands while
    // bootstrap is in flight, this effect used to fire against a null agent
    // (no-op) and never re-run — the stock "Name (Partner)" label stuck.
    // Re-asserting on every agent identity change also survives any later
    // setAgent from session refresh/recovery paths.
  }, [overrideDisplayName, overrideAvatarUrl, agent]);

  const retry = useCallback(() => {
    bootstrappedForUser.current = null;
    setChatSessionId(null);
    setAgent(null);
    setStatus('idle');
    setError(null);
    setRetryToken(t => t + 1);
  }, []);

  const value = useMemo<ChatSessionContextValue>(
    () => ({ chatSessionId, agent, status, error, retry }),
    [chatSessionId, agent, status, error, retry],
  );

  return (
    <ChatSessionContext.Provider value={value}>
      {children}
    </ChatSessionContext.Provider>
  );
};

export function useChatSession(): ChatSessionContextValue {
  const ctx = useContext(ChatSessionContext);
  if (!ctx) {
    throw new Error('useChatSession must be used within <ChatSessionProvider>');
  }
  return ctx;
}
